import { PROGRAMS, MapleProgram } from '@/OS/utils/programs';
import fileExplorerMarkdown from './docs/file-explorer.md?raw';

export type AssistantCommandContext = {
  openApp: (appId: string, title: string, filePath?: string, content?: string) => void;
  createFolder: (path: string) => Promise<boolean>;
  createFile: (path: string, content?: string) => Promise<boolean>;
  ensureDir: (path: string) => Promise<void>;
  pathExists: (path: string) => boolean;
  resolveDocumentsPath: () => Promise<string>;
};

export type AssistantCommandResult =
  | { matched: false }
  | { matched: true; success: true; shouldClose?: boolean; message?: string }
  | { matched: true; success: false; error: string };

export type AssistantAppAction = {
  id: string;
  title: string;
  examples: string[];
  run: (command: string, context: AssistantCommandContext) => Promise<AssistantCommandResult>;
};

export type AssistantAppCapability = {
  appId: string;
  title: string;
  markdown: string;
  actions: AssistantAppAction[];
};

const INVALID_PATH_CHARS = /[<>:"/\\|?*]+/g;

const sanitizePathSegment = (value: string) => value.replace(INVALID_PATH_CHARS, '').trim();

const buildGenericMarkdown = (program: MapleProgram) => `# ${program.label}\n\n## Purpose\n${program.description || `${program.label} is available inside MapleOS.`}\n\n## Assistant integration\n- The assistant can open this app by name.\n- No direct app-specific callable actions are registered yet.\n`;

const extractNamedValue = (command: string) => {
  const quoted = command.match(/(?:called|named)\s+["']([^"']+)["']/i);
  if (quoted?.[1]) return sanitizePathSegment(quoted[1]);

  const plain = command.match(/(?:called|named)\s+([^.,]+)$/i);
  if (plain?.[1]) return sanitizePathSegment(plain[1]);

  return '';
};

const resolveBasePath = async (command: string, resolveDocumentsPath: () => Promise<string>) => {
  const normalized = command.toLowerCase();
  const documentsPath = await resolveDocumentsPath();
  const userRoot = documentsPath.replace(/\\Documents$/i, '');

  if (/\bdesktop\b/.test(normalized)) return `${userRoot}\\Desktop`;
  if (/\bdocuments?\b/.test(normalized)) return documentsPath;
  if (/\bdownloads?\b/.test(normalized)) return `${userRoot}\\Downloads`;
  if (/\bpictures?\b/.test(normalized)) return `${userRoot}\\Pictures`;
  if (/\bmusic\b/.test(normalized)) return `${userRoot}\\Music`;
  if (/\bvideos?\b/.test(normalized)) return `${userRoot}\\Videos`;
  if (/\b(?:c\s*drive|c:)\b/.test(normalized)) return 'C:\\';

  return 'C:\\';
};

const fileExplorerActions: AssistantAppAction[] = [
  {
    id: 'file-explorer.create-folder',
    title: 'Create folder',
    examples: [
      'open my computer and make a folder on the c drive called test',
      'create a folder in documents called Drafts',
      'make a folder on the desktop called Screenshots',
    ],
    async run(command, context) {
      if (!/\b(?:make|create|new)\s+(?:a\s+)?folder\b/i.test(command)) {
        return { matched: false };
      }

      const folderName = extractNamedValue(command);
      if (!folderName) {
        return { matched: true, success: false, error: 'Tell me the folder name using words like "called" or "named".' };
      }

      const basePath = await resolveBasePath(command, context.resolveDocumentsPath);
      const targetPath = basePath.endsWith('\\') ? `${basePath}${folderName}` : `${basePath}\\${folderName}`;

      await context.ensureDir(basePath);

      if (!context.pathExists(targetPath)) {
        const created = await context.createFolder(targetPath);
        if (!created) {
          return { matched: true, success: false, error: `I couldn't create the folder at ${targetPath}.` };
        }
      }

      context.openApp('file-explorer', basePath === 'My Computer' ? 'My Computer' : 'File Explorer', basePath);
      return { matched: true, success: true, shouldClose: true, message: `Folder ready at ${targetPath}` };
    },
  },
  {
    id: 'file-explorer.create-file',
    title: 'Create file',
    examples: [
      'make a file on C:\\ called "testme"',
      'create a file in documents called notes.txt',
      'make a text file on the desktop called todo.txt',
    ],
    async run(command, context) {
      if (!/\b(?:make|create|new)\s+(?:a\s+)?(?:text\s+)?file\b/i.test(command)) {
        return { matched: false };
      }

      const fileName = extractNamedValue(command);
      if (!fileName) {
        return { matched: true, success: false, error: 'Tell me the file name using words like "called" or "named".' };
      }

      const basePath = await resolveBasePath(command, context.resolveDocumentsPath);
      const targetPath = basePath.endsWith('\\') ? `${basePath}${fileName}` : `${basePath}\\${fileName}`;

      await context.ensureDir(basePath);

      if (!context.pathExists(targetPath)) {
        const created = await context.createFile(targetPath, '');
        if (!created) {
          return { matched: true, success: false, error: `I couldn't create the file at ${targetPath}.` };
        }
      }

      context.openApp('file-explorer', basePath === 'My Computer' ? 'My Computer' : 'File Explorer', basePath);
      return { matched: true, success: true, shouldClose: true, message: `File ready at ${targetPath}` };
    },
  },
  {
    id: 'file-explorer.open-my-computer',
    title: 'Open My Computer',
    examples: ['open my computer', 'open file explorer'],
    async run(command, context) {
      if (!/^open\s+(my\s+computer|file\s*explorer)$/i.test(command.trim())) {
        return { matched: false };
      }

      context.openApp('file-explorer', 'My Computer', 'My Computer');
      return { matched: true, success: true, shouldClose: true };
    },
  },
];

const markdownOverrides: Record<string, string> = {
  'file-explorer': fileExplorerMarkdown,
};

const actionOverrides: Record<string, AssistantAppAction[]> = {
  'file-explorer': fileExplorerActions,
};

export const ASSISTANT_APP_CAPABILITIES: AssistantAppCapability[] = PROGRAMS.map((program) => ({
  appId: program.id,
  title: program.label,
  markdown: markdownOverrides[program.id] || buildGenericMarkdown(program),
  actions: actionOverrides[program.id] || [],
}));

export const getAssistantAppMarkdown = (appId: string) =>
  ASSISTANT_APP_CAPABILITIES.find((item) => item.appId === appId)?.markdown || null;

export async function runAssistantAppCommand(command: string, context: AssistantCommandContext): Promise<AssistantCommandResult> {
  for (const capability of ASSISTANT_APP_CAPABILITIES) {
    for (const action of capability.actions) {
      const result = await action.run(command, context);
      if (result.matched) {
        return result;
      }
    }
  }

  return { matched: false };
}